<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
      <!-- Cash-plan status (#4798): whether this request has been picked up into a weekly cash request, and
           which one(s) — a request can be funded across more than one week. -->
      @if (isPersisted()) {
        @for (cashRequest of cashRequestReferences(); track cashRequest.reference) {
          <p-tag severity="info" [value]="cashRequest.number ?? ''" />
        } @empty {
          <p-tag severity="secondary" [value]="'ApPaymentRequest_NotInCashPlan' | translateWF: 'Not in a cash plan'" />
        }
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (spawnedPaymentId()) {
        <p-button [label]="'ApPaymentRequest_ViewPayment' | translateWF: 'View payment'" icon="pi pi-wallet" variant="outlined" (click)="openSpawnedPayment()" />
      }
      @if (isEditable()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" />
      }
      @if (isDraft() && isPersisted()) {
        <p-button [label]="'ApPaymentRequest_Submit' | translateWF: 'Submit'" icon="pi pi-send" (click)="submit()" />
      }
      @if (isPendingApproval() && isApprover) {
        <p-button [label]="'ApPaymentRequest_Approve' | translateWF: 'Approve'" icon="pi pi-check" severity="success" (click)="approve()" />
        <p-button [label]="'ApPaymentRequest_Reject' | translateWF: 'Reject'" icon="pi pi-times" severity="danger" variant="outlined" (click)="reject()" />
      }
      @if (isPersisted() && isEditable()) {
        <p-button [label]="'Cancel' | translateWF: 'Cancel'" icon="pi pi-ban" severity="secondary" variant="text" (click)="cancel()" />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-8 col">
        <!-- Request -->
        <p-panel [header]="'ApPaymentRequest_Section' | translateWF: 'Payment Request'" [toggleable]="true">
          <p-card>
            <div class="grid-12">
              <div class="span-12-6">
                <div class="form-box">
                  <label for="provider">{{ 'ApPayment_Provider' | translateWF: 'Provider' }} <span class="text-red-500">*</span></label>
                  <!-- `class` sizes the host, `styleClass` the inner input — the typeahead needs BOTH to fill
                     its column the way the plain p-select beside it does. -->
                  <p-autoComplete
                    id="provider"
                    formControlName="provider"
                    #providerSearch="cslServerSearch"
                    [cslServerSearch]="fetchProviders"
                    [suggestions]="providerSearch.results()"
                    (onSelect)="onProviderSelected($event.value)"
                    (onClear)="onProviderCleared()"
                    optionLabel="name"
                    [forceSelection]="true"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [minLength]="1"
                    appendTo="body"
                    class="w-full"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': form.controls.provider.invalid && form.controls.provider.touched }"
                  >
                    <ng-template let-business pTemplate="item">
                      <div class="col">
                        <span
                          ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                        >
                      </div>
                    </ng-template>
                  </p-autoComplete>
                  @if (selectedProvider(); as prov) {
                    <div class="row-sm-between-center mt-1">
                      <!-- min-w-0 + truncate: a long legal name must not push the Reload button out of a half column. -->
                      <small class="text-surface-500 block min-w-0 truncate" [title]="prov.code + ' — ' + prov.name"
                        ><strong>{{ prov.code }}</strong> — {{ prov.name }}</small
                      >
                      <!-- A provider is often picked before its banking details exist; re-fetch them in place
                         rather than making the user reload the page and lose the amounts typed so far. -->
                      @if (isEditable()) {
                        <p-button
                          [label]="'ApPaymentRequest_ReloadProvider' | translateWF: 'Reload'"
                          icon="pi pi-refresh"
                          size="small"
                          variant="text"
                          (click)="reloadProvider()"
                          [pTooltip]="'ApPaymentRequest_ReloadProvider_Tip' | translateWF: 'Re-fetch this provider (e.g. after adding a bank account) without clearing the lines.'"
                        />
                      }
                    </div>
                  } @else {
                    <small class="text-surface-500">{{ 'ApPaymentRequest_Provider_Hint' | translateWF: 'The provider is locked once the request is created.' }}</small>
                  }
                </div>
              </div>
              <div class="span-12-6">
                <div class="form-box">
                  <label for="bank">{{ 'ApPaymentRequest_DestinationBank' | translateWF: 'Destination bank' }}</label>
                  <p-select
                    id="bank"
                    formControlName="selectedProviderBankId"
                    [options]="bankOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"
                    [placeholder]="'ApPaymentRequest_SelectBank' | translateWF: 'Select a provider bank'"
                  />
                  @if (selectedProvider() && bankOptions().length === 0) {
                    <small class="text-amber-600">{{
                      'ApPaymentRequest_NoBanks_Hint' | translateWF: 'This provider has no bank accounts yet — add one on the provider, then press Reload.'
                    }}</small>
                  } @else {
                    <small class="text-surface-500">{{ 'ApPaymentRequest_Bank_Hint' | translateWF: 'From the provider master; frozen as a snapshot on approval.' }}</small>
                  }
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Invoices -->
        <p-panel [toggleable]="true">
          <ng-template pTemplate="header">
            <div class="row-between-center w-full">
              <span class="font-semibold">{{ 'ApPaymentRequest_Section_Invoices' | translateWF: 'Invoices to pay' }} <span class="text-red-500">*</span></span>
              <span class="text-sm text-surface-500">{{ selectedCount() }} {{ 'ApPaymentRequest_Selected' | translateWF: 'selected' }}</span>
            </div>
          </ng-template>

          <p-card data-variant="compact">
            <!-- Finding a document in a long list. A view only: hidden rows keep their selection and amounts, and
               the totals below still count every selected row. -->
            @if (lines.length > 0) {
              <div class="row-start-center flex-wrap mb-3">
                <p-iconfield class="w-full md:w-80">
                  <p-inputicon class="pi pi-search" />
                  <input
                    pInputText
                    class="w-full"
                    [formControl]="searchControl"
                    [placeholder]="'ApPaymentRequest_Filter_Search' | translateWF: 'Search AP #, provider invoice # or PO'"
                  />
                </p-iconfield>
                @if (isEditable()) {
                  <div class="row-sm-start-center">
                    <p-checkbox inputId="selected-only" [binary]="true" [formControl]="selectedOnlyControl" />
                    <label for="selected-only" class="cursor-pointer"> {{ 'ApPaymentRequest_Filter_SelectedOnly' | translateWF: 'Selected only' }} ({{ selectedCount() }}) </label>
                  </div>
                }
                @if (isFiltered()) {
                  <span class="text-sm text-surface-500"> {{ visibleLines().length }} / {{ lines.length }} {{ 'ApPaymentRequest_Filter_Shown' | translateWF: 'shown' }} </span>
                }
              </div>
            }

            <p-table
              [value]="visibleLines()"
              [paginator]="lines.length > pageSize"
              [rows]="pageSize"
              [rowsPerPageOptions]="[25, 50, 100]"
              [first]="firstRow()"
              (onPage)="firstRow.set($event.first)"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 3rem">
                    <!-- Select / clear every VISIBLE selectable invoice (#5224). Withholding-blocked rows are
                       excluded, so this can never tick a row the API would reject; rows hidden by the search
                       are left alone. -->
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="allSelected()"
                      [ngModelOptions]="{ standalone: true }"
                      (ngModelChange)="toggleSelectAll($event)"
                      [pTooltip]="'ApPaymentRequest_SelectAll' | translateWF: 'Select or clear all'"
                    />
                  </th>
                  <th>{{ 'ApPayment_Invoice' | translateWF: 'Invoice' }}</th>
                  <th>{{ 'ApPayment_DueDate' | translateWF: 'Due date' }}</th>
                  <th class="text-right">{{ 'ApPayment_NetPayable' | translateWF: 'Net payable' }}</th>
                  <th class="text-right" style="width: 11rem">{{ 'ApPaymentRequest_AmountToPay' | translateWF: 'Amount to pay' }}</th>
                  <th class="text-right" style="width: 11rem">{{ 'ApPayment_AdvanceToUse' | translateWF: 'Advance to use' }}</th>
                  <th class="text-right">{{ 'ApPayment_CashToPay' | translateWF: 'Cash to pay' }}</th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-row>
                @let line = getRowGroup(row);
                @let docId = line.controls.apDocumentReference.value;
                @let payable = payableFor(docId);
                @let withholdingBlocked = line.controls.withholdingBlocked.value;
                <tr [formGroup]="line" [class.opacity-60]="withholdingBlocked">
                  <td><p-checkbox formControlName="selected" [binary]="true" [disabled]="withholdingBlocked" /></td>
                  <td>
                    <!-- Fiscal number over our own (#4788); the AP number opens the read-only quick view (#4789),
                       which stays enabled even on a disabled "Settled" row. -->
                    <app-document-identity
                      [documentNumber]="line.controls.invoiceNumber.value"
                      [fiscalNumber]="line.controls.fiscalNumber.value"
                      [linkTitle]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                      (documentClick)="openDocumentQuickView(docId)"
                    >
                      @if (line.controls.isCredit.value) {
                        <p-tag severity="warn" [value]="'ApPaymentRequest_Credit' | translateWF: 'Credit'" />
                      }
                      <!-- A previously-selected doc the payable search no longer returns (already settled) — shown read-only (#4457). -->
                      @if (!payable) {
                        <p-tag
                          severity="secondary"
                          [value]="'ApPaymentRequest_Settled' | translateWF: 'Settled'"
                          [title]="'ApPaymentRequest_Settled_Hint' | translateWF: 'Already settled — no longer in the open payables, kept here as part of this request.'"
                        />
                      }
                      @if (payable?.hasAdjustments) {
                        <i class="pi pi-info-circle text-amber-500 cursor-pointer" [title]="adjustmentTooltip(docId)" (click)="toggleExpand(docId)"></i>
                      }
                      <!-- #4488/#4842 — shown rather than hidden, so the clerk can see what is waiting on the
                         Financial Officer instead of an invoice that simply isn't there. -->
                      @if (withholdingBlocked) {
                        <p-tag
                          severity="danger"
                          [title]="
                            'ApDocument_Withholding_Blocked_Hint'
                              | translateWF: 'This document needs a posted withholding certificate before it can be paid. A Financial Officer must issue one first.'
                          "
                        >
                          <i class="pi pi-lock mr-1"></i>{{ 'ApDocument_Withholding_Blocked' | translateWF: 'Requires Constancia de Retención' }}
                        </p-tag>
                      }
                      <!-- #5662 — advice, not a lock: the row stays selectable and the payment post decides. -->
                      <app-received-coverage-tag [coverage]="receivedCoverage().get(docId)" />
                    </app-document-identity>
                  </td>
                  <td>{{ line.controls.dueDate.value ? (line.controls.dueDate.value | date: 'dd/MM/yyyy') : '—' }}</td>
                  <td class="text-right">{{ line.controls.netPayable.value | number: '1.2-2' }} {{ lineCurrencyLabel(line) }}</td>
                  <td class="text-right">
                    <!-- Grouped amount, distributed doc-first on submit. A standalone credit row is NEGATIVE (#4477):
                       its netPayable is already negative, so min = netPayable, max = 0. -->
                    <p-inputNumber
                      formControlName="amountToPay"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="line.controls.isCredit.value ? line.controls.netPayable.value : 0"
                      [max]="line.controls.isCredit.value ? 0 : line.controls.netPayable.value"
                      styleClass="w-full"
                    />
                    <!-- Converted at THIS document's own booked rate — what the line contributes in USD. -->
                    <small class="text-surface-400 block tabular-nums"> {{ functionalFor(line) | number: '1.2-2' }} {{ functionalLabel() }} </small>
                  </td>
                  <td class="text-right">
                    <!-- Advance to use (#4349): auto-filled to what's available on this invoice's PO(s); lower it to
                       pay more cash. Hidden when the PO has no advance. -->
                    @if (line.controls.advanceAvailable.value > 0) {
                      <p-inputNumber
                        formControlName="advanceToUse"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [min]="0"
                        [max]="line.controls.advanceAvailable.value"
                        styleClass="w-full"
                      />
                      <small class="text-surface-400 block">
                        {{ 'ApPayment_AdvanceAvail' | translateWF: 'avail.' }} {{ line.controls.advanceAvailable.value | number: '1.2-2' }}
                      </small>
                    } @else {
                      <span class="text-surface-400">—</span>
                    }
                  </td>
                  <td class="text-right font-medium">{{ cashToPay(line) | number: '1.2-2' }} {{ lineCurrencyLabel(line) }}</td>
                </tr>

                <!-- Justification (#4787). Appears only once the row pays something other than what is owed, and is
                   required before the request can be submitted — the approver sees the number and the reason together. -->
                @if (hasVariance(line)) {
                  <tr [formGroup]="line" class="note-row">
                    <td></td>
                    <td colspan="6">
                      <label class="block text-sm mb-1">
                        {{ 'ApPaymentRequest_LineNote' | translateWF: 'Why is this different from the net payable?' }}
                        <span class="text-red-500">*</span>
                      </label>
                      <input
                        pInputText
                        formControlName="note"
                        class="w-full"
                        [placeholder]="'ApPaymentRequest_LineNote_Placeholder' | translateWF: 'e.g. partial delivery, disputed freight, agreed short payment'"
                        [ngClass]="{ 'ng-invalid ng-dirty': needsNote(line) && line.controls.note.touched }"
                      />
                      @if (needsNote(line) && line.controls.note.touched) {
                        <small class="text-red-500">
                          {{ 'ApPaymentRequest_LineNote_Required' | translateWF: 'A justification is required to submit this request.' }}
                        </small>
                      }
                    </td>
                  </tr>
                }
                @if (payable && expandedRows[docId]) {
                  <tr class="resumen-row">
                    <td></td>
                    <td colspan="6">
                      <app-payable-document-summary [payable]="payable" [currencyLabel]="lineCurrencyLabel(line)" />
                    </td>
                  </tr>
                }
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="7">
                    @if (lines.length > 0) {
                      {{ 'ApPaymentRequest_Filter_NoMatch' | translateWF: 'No invoices match the filter.' }}
                    } @else {
                      {{ 'ApPaymentRequest_NoInvoices' | translateWF: 'Select a provider to load its open invoices.' }}
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-card>
        </p-panel>

        <!-- Provider credits (#6324): open credit notes, used like an advance. The requester picks which and how
             much; nothing is selected by default and whatever is not used stays open. -->
        @if (creditLines().length > 0) {
          <p-panel [toggleable]="true">
            <ng-template pTemplate="header">
              <div class="row-between-center w-full">
                <span class="font-semibold">{{ 'ApPaymentRequest_Section_ProviderCredits' | translateWF: 'Provider credits' }}</span>
                <span class="text-sm text-surface-500">{{
                  'ApPaymentRequest_ProviderCredits_Hint' | translateWF: 'Credits the provider owes us — apply them to reduce what is paid.'
                }}</span>
              </div>
            </ng-template>
            <p-card data-variant="compact">
              <p-table [value]="creditLines()" [rowHover]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"></th>
                    <th>{{ 'ApDocument_CreditNote' | translateWF: 'Credit note' }}</th>
                    <th class="text-right">{{ 'ApPaymentRequest_Credit_Available' | translateWF: 'Available' }}</th>
                    <th class="text-right" style="width: 12rem">{{ 'ApPaymentRequest_Credit_Apply' | translateWF: 'Apply' }}</th>
                    <th class="text-right">{{ 'ApPaymentRequest_Credit_LeftOpen' | translateWF: 'Stays open' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  @let line = getRowGroup(row);
                  @let docId = line.controls.apDocumentReference.value;
                  <tr [formGroup]="line">
                    <td><p-checkbox formControlName="selected" [binary]="true" /></td>
                    <td>
                      <app-document-identity
                        [documentNumber]="line.controls.invoiceNumber.value"
                        [fiscalNumber]="line.controls.fiscalNumber.value"
                        [linkTitle]="'ApDocument_QuickView_Open' | translateWF: 'View document detail'"
                        (documentClick)="openDocumentQuickView(docId)"
                      />
                    </td>
                    <td class="text-right tabular-nums">{{ -line.controls.netPayable.value | number: '1.2-2' }} {{ lineCurrencyLabel(line) }}</td>
                    <td class="text-right">
                      <p-inputNumber
                        [ngModel]="-line.controls.amountToPay.value"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onCreditAppliedChange(line, $event)"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [min]="0"
                        [max]="-line.controls.netPayable.value"
                        [disabled]="!line.controls.selected.value || !isEditable()"
                        styleClass="w-full"
                      />
                    </td>
                    <td class="text-right tabular-nums">{{ creditLeftOpen(line) | number: '1.2-2' }} {{ lineCurrencyLabel(line) }}</td>
                  </tr>
                </ng-template>
              </p-table>
              @if (creditsExceedCharges()) {
                <p-message
                  severity="warn"
                  styleClass="w-full mt-3"
                  [text]="
                    'ApPaymentRequest_CreditsExceedCharges'
                      | translateWF: 'The credits applied cover everything selected. Select more invoices or apply less credit — a request has to pay something.'
                  "
                />
              }
            </p-card>
          </p-panel>
        }

        <!-- Notes -->
        <p-panel [header]="'ApPayment_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
          <p-card>
            <div class="form-box">
              <textarea pTextarea formControlName="notes" rows="3" class="w-full" placeholder="Add a note…"></textarea>
            </div>
          </p-card>
        </p-panel>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="span-12-12-4 col sticky-summary">
        <p-panel [header]="'ApPaymentRequest_Summary' | translateWF: 'Request summary'">
          <p-card>
            <p class="text-sm text-surface-500 mb-3">
              {{ 'ApPaymentRequest_Summary_Hint' | translateWF: 'On approval a draft AP payment is created for the executing department.' }}
            </p>
            <!-- Headline is the functional total whenever the selection spans currencies: the source amounts
               cannot be added, and the functional figure is the one the money actually leaves as. -->
            <div class="ap-request-total">
              <span class="ap-request-total__label">{{ 'ApPaymentRequest_Total' | translateWF: 'Total to settle' }}</span>
              @if (isMixedCurrency()) {
                <span class="ap-request-total__value">{{ totalFunctional() | number: '1.2-2' }} {{ functionalLabel() }}</span>
              } @else {
                <span class="ap-request-total__value">{{ total() | number: '1.2-2' }} {{ selectionCurrencyLabel() }}</span>
              }
            </div>

            @if (isMixedCurrency()) {
              <p class="text-xs text-surface-500 mt-2">
                {{ 'ApPaymentRequest_MixedCurrency' | translateWF: 'The selected invoices are in more than one currency, so they are totalled in' }}
                {{ functionalLabel() }} {{ 'ApPaymentRequest_MixedCurrency_Rate' | translateWF: '— each at the rate its own invoice was booked with.' }}
              </p>
            } @else {
              <!-- Summed per line at each document's own booked rate, never the source total times one rate:
                 invoices booked on different days do not share a rate, and no single one reproduces the pair. -->
              <div class="row-between-center text-sm mt-2 text-surface-600">
                <span>{{ 'ApPaymentRequest_TotalFunctional' | translateWF: 'Total in' }} {{ functionalLabel() }}</span>
                <span class="tabular-nums">{{ totalFunctional() | number: '1.2-2' }} {{ functionalLabel() }}</span>
              </div>
            }

            @if (creditAppliedFunctional() > 0) {
              <div class="row-between-center text-sm mt-2 text-surface-600">
                <span>{{ 'ApPaymentRequest_ProviderCreditApplied' | translateWF: 'Provider credit applied' }}</span>
                <span class="tabular-nums">− {{ creditAppliedFunctional() | number: '1.2-2' }} {{ functionalLabel() }}</span>
              </div>
            }
            @if (totalAdvance() > 0) {
              <div class="row-between-center text-sm mt-2 text-surface-600">
                <span>{{ 'ApPayment_AdvanceApplied' | translateWF: 'Advance applied' }}</span>
                @if (isMixedCurrency()) {
                  <span>− {{ totalAdvanceFunctional() | number: '1.2-2' }} {{ functionalLabel() }}</span>
                } @else {
                  <span>− {{ totalAdvance() | number: '1.2-2' }} {{ selectionCurrencyLabel() }}</span>
                }
              </div>
              <div class="row-between-center text-sm font-semibold mt-1 pt-1 border-t border-surface-200">
                <span>{{ 'ApPayment_CashToPay' | translateWF: 'Cash to pay' }}</span>
                @if (isMixedCurrency()) {
                  <span>{{ totalCashFunctional() | number: '1.2-2' }} {{ functionalLabel() }}</span>
                } @else {
                  <span>{{ totalCash() | number: '1.2-2' }} {{ selectionCurrencyLabel() }}</span>
                }
              </div>
            }

            <!-- What the money can actually leave as. A payment settles out of an account in its own currency
               and may only settle a foreign document when it is itself in the functional currency (#5144). -->
            @if (destinationCurrency() !== null) {
              <div class="row-between-center text-sm mt-2 text-surface-600">
                <span>{{ 'ApPaymentRequest_DestinationCurrency' | translateWF: 'Destination account' }}</span>
                <span class="tabular-nums">{{ destinationLabel() }}</span>
              </div>
            }
            <app-received-coverage-banner class="block mt-2" [coverages]="selectedCoverage()" />
            @if (destinationMismatch()) {
              <p-message severity="warn" styleClass="w-full mt-2">
                <span class="text-sm">
                  {{
                    'ApPaymentRequest_DestinationMismatch'
                      | translateWF
                        : 'The destination account does not hold the currency of every selected invoice, and is not the functional currency — the payment will be refused. Choose an account in the functional currency, or select invoices in the destination currency only.'
                  }}
                </span>
              </p-message>
            }
            <p class="text-xs text-surface-500 mt-2">
              {{
                'ApPaymentRequest_NetHint'
                  | translateWF
                    : 'Balances are already net of any withholding (Constancia de Retención). Advances (Anticipos) on a PO auto-fill and reduce the cash. Expand a flagged row for its credit/debit notes.'
              }}
            </p>
          </p-card>
        </p-panel>
      </div>
    </form>
  </div>
</main>
